import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/header";
import DefaultFooter from "../components/default-footer";

// ============================================================
// Small reusable piece — same dune-silhouette rule used across
// the site, so this page doesn't read as a bolted-on screen.
// ============================================================
function DuneRule({ tone = "light" }: { tone?: "light" | "dark" }) {
  const stroke = tone === "dark" ? "rgba(255,255,255,.14)" : "rgba(41,75,90,.14)";
  return (
    <div aria-hidden="true" style={{ lineHeight: 0, background: "inherit" }}>
      <svg viewBox="0 0 1200 34" width="100%" height="22" preserveAspectRatio="none">
        <path
          d="M0 24 C 90 4, 170 4, 250 20 S 400 30, 480 14 S 620 2, 700 18 S 850 30, 940 12 S 1090 2, 1200 16"
          fill="none" stroke="var(--gold-d)" strokeOpacity="0.55" strokeWidth="1.5"
        />
        <path
          d="M0 28 C 90 12, 170 12, 250 26 S 400 34, 480 20 S 620 10, 700 24 S 850 34, 940 18 S 1090 10, 1200 22"
          fill="none" stroke={stroke} strokeWidth="1"
        />
      </svg>
    </div>
  );
}

// A shield-and-compass mark — protection + travel, fits an
// insurance product for a travel agency better than a generic icon.
function ShieldIllustration() {
  return (
    <svg width="180" height="180" viewBox="0 0 200 200" fill="none" aria-hidden="true">
      <circle cx="100" cy="100" r="92" stroke="var(--gold)" strokeOpacity="0.3" strokeWidth="1.5" />
      <circle cx="100" cy="100" r="72" stroke="var(--gold)" strokeOpacity="0.45" strokeWidth="1" />

      {/* Shield outline */}
      <path
        d="M100 34 L146 52 V96 C146 128 126 152 100 164 C74 152 54 128 54 96 V52 Z"
        fill="var(--teal-d)"
        fillOpacity="0.05"
        stroke="var(--teal)"
        strokeWidth="1.5"
      />
      <path
        d="M100 34 L146 52 V96 C146 128 126 152 100 164 C74 152 54 128 54 96 V52 Z"
        fill="none"
        stroke="var(--gold)"
        strokeOpacity="0.6"
        strokeWidth="1"
        transform="scale(0.92) translate(9 9)"
      />

      {/* Checkmark */}
      <path
        d="M78 100 L94 116 L124 82"
        fill="none"
        stroke="var(--gold)"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <circle cx="100" cy="100" r="4" fill="var(--teal-d)" opacity="0" />
    </svg>
  );
}

function Insurance() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleNotifySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    // Front-end only for now — wire this up to your API once the
    // insurance product / waitlist endpoint is ready.
    setSubmitted(true);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
        }
        .ins-page { font-family: 'DM Sans', sans-serif; background: var(--cream); }
        .ins-page h1, .ins-page h2 { font-family: 'Playfair Display', serif; }

        .ins-hero {
          min-height: 74vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 160px 24px 80px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .ins-hero::before {
          content: '';
          position: absolute; inset: 0;
          background: radial-gradient(55% 55% at 50% 20%, rgba(211,190,118,.10), transparent 70%);
          pointer-events: none;
        }
        @media (max-width: 640px) { .ins-hero { padding: 128px 20px 60px; } }

        .ins-content { position: relative; max-width: 560px; margin: 0 auto; }

        .ins-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.5px;
          color: var(--gold-d);
          background: rgba(211,190,118,.12);
          border: 1px solid rgba(211,190,118,.35);
          border-radius: 30px;
          padding: 7px 16px;
          margin-top: 18px;
          margin-bottom: 22px;
        }
        .ins-eyebrow::before {
          content: '';
          width: 6px; height: 6px;
          border-radius: 50%;
          background: var(--gold-d);
        }

        .ins-title { font-size: clamp(1.7rem, 3vw, 2.3rem); font-weight: 600; color: var(--ink); margin: 0 0 14px; line-height: 1.2; }
        .ins-title em { color: var(--gold-d); font-style: italic; font-weight: 500; }
        .ins-text { font-size: 15px; color: var(--silver); line-height: 1.75; font-weight: 300; margin-bottom: 34px; }

        .ins-notify {
          display: flex;
          gap: 10px;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 22px;
        }
        .ins-notify input {
          flex: 1;
          min-width: 220px;
          max-width: 300px;
          font-size: 13px;
          font-family: 'DM Sans', sans-serif;
          color: var(--ink);
          background: #fff;
          border: 1px solid rgba(41,75,90,.16);
          border-radius: 5px;
          padding: 13px 16px;
          outline: none;
          transition: border-color .2s, box-shadow .2s;
        }
        .ins-notify input:focus {
          border-color: var(--teal);
          box-shadow: 0 0 0 3px rgba(41,75,90,.08);
        }
        .ins-notify input::placeholder { color: var(--silver); }

        .ins-btn-primary {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--gold); color: var(--teal-d);
          border: none;
          border-radius: 5px; font-size: 12px; font-weight: 700; letter-spacing: .3px;
          padding: 14px 24px; text-decoration: none;
          cursor: pointer;
          transition: transform .2s, background .2s, box-shadow .2s;
          box-shadow: 0 10px 28px rgba(211,190,118,.28);
          white-space: nowrap;
        }
        .ins-btn-primary:hover { background: #e2ce86; transform: translateY(-2px); color: var(--teal-d); }

        .ins-confirmed {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          color: var(--teal);
          background: rgba(41,75,90,.06);
          border: 1px solid rgba(41,75,90,.14);
          border-radius: 30px;
          padding: 10px 20px;
          margin-bottom: 22px;
        }

        .ins-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-bottom: 40px; }
        .ins-btn-ghost {
          display: inline-flex; align-items: center;
          background: transparent; color: var(--ink);
          border: 1px solid rgba(41,75,90,.2);
          border-radius: 5px; font-size: 12px; font-weight: 600; letter-spacing: .3px;
          padding: 14px 24px; text-decoration: none;
          transition: background .2s, border-color .2s;
        }
        .ins-btn-ghost:hover { border-color: var(--teal); background: rgba(41,75,90,.04); }

        /* Quick links — keeps someone exploring instead of bouncing */
        .ins-links { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }
        .ins-link-pill {
          font-size: 12px; font-weight: 600; color: var(--teal);
          background: rgba(41,75,90,.06); border: 1px solid rgba(41,75,90,.1);
          border-radius: 30px; padding: 8px 16px; text-decoration: none;
          transition: border-color .2s, color .2s, background .2s;
        }
        .ins-link-pill:hover { border-color: var(--gold-d); color: var(--gold-d); background: rgba(211,190,118,.08); }
      `}</style>

      <div className="ins-page">
        <Header />

        <section className="ins-hero">
          <div className="ins-content">
            <ShieldIllustration />

            <div className="ins-eyebrow">Coming Soon</div>

            <h1 className="ins-title">
              Travel <em>Insurance</em> is on its way
            </h1>
            <p className="ins-text">
              We're putting the finishing touches on flexible, worry-free travel insurance plans —
              so you can book with confidence, wherever the trip takes you.
              Leave your email and we'll let you know the moment it's live.
            </p>

            {submitted ? (
              <div className="ins-confirmed">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                You're on the list — we'll email you when it launches.
              </div>
            ) : (
              <form className="ins-notify" onSubmit={handleNotifySubmit}>
                <input
                  type="email"
                  required
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button type="submit" className="ins-btn-primary">
                  Notify Me
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              </form>
            )}

            <div className="ins-actions">
              <Link to="/" className="ins-btn-ghost">Back to Home</Link>
              <Link to="/contact" className="ins-btn-ghost">Contact Us</Link>
            </div>

            <div className="ins-links">
              <Link to="/search-visa" className="ins-link-pill">Visa Services</Link>
              <Link to="/trips" className="ins-link-pill">Tours & Travel</Link>
              <Link to="/car-rent" className="ins-link-pill">Car Rental</Link>
              <Link to="/flights" className="ins-link-pill">Flights</Link>
            </div>
          </div>
        </section>

        <DuneRule />
        <DefaultFooter />
      </div>
    </>
  );
}

export default Insurance;